import { useCallback } from 'react';

import type { ColumnSort } from '@tanstack/react-table';
import { create } from 'zustand';

import {
  persistTableSetting,
  readConsolidatedTableSettings,
  type TableDensity,
} from './persist';

import type { SetStateAction } from './app-store';

const PAGE_SIZE = 15;

export type TableSlice = {
  pageIndex: number;
  pageSize: number;
  pageRecords: string[];
  totalRecords: number;
  filters: Record<string, string>;
  sorting: ColumnSort[];
  density: TableDensity;
  resultsPerPage: number | undefined;
};

const EMPTY_FILTERS: Record<string, string> = {};
const EMPTY_SORTING: ColumnSort[] = [];
const EMPTY_RECORDS: string[] = [];

function defaultSlice(): TableSlice {
  return {
    pageIndex: 0,
    pageSize: PAGE_SIZE,
    pageRecords: [],
    totalRecords: 0,
    filters: {},
    sorting: [],
    density: 'default',
    resultsPerPage: undefined,
  };
}

function hydrateSlice(tableId: string): TableSlice {
  const slice = defaultSlice();
  const persisted = readConsolidatedTableSettings()[tableId];
  if (persisted?.density) {
    slice.density = persisted.density;
  }
  if (persisted && 'resultsPerPage' in persisted) {
    slice.resultsPerPage = persisted.resultsPerPage;
  }
  return slice;
}

function resolveUpdater<T>(prev: T, updater: SetStateAction<T>): T {
  return typeof updater === 'function'
    ? (updater as (prev: T) => T)(prev)
    : updater;
}

type TableClientState = {
  tables: Record<string, TableSlice>;
};

export const useTableClientStore = create<TableClientState>(() => ({
  tables: {},
}));

function getSlice(tableId: string): TableSlice {
  return (
    useTableClientStore.getState().tables[tableId] ?? hydrateSlice(tableId)
  );
}

function fieldValuesEqual<T>(prev: T, next: T): boolean {
  if (Object.is(prev, next)) return true;
  if (Array.isArray(prev) && Array.isArray(next)) {
    if (prev.length !== next.length) return false;
    return prev.every((item, index) => Object.is(item, next[index]));
  }
  if (
    prev !== null &&
    next !== null &&
    typeof prev === 'object' &&
    typeof next === 'object'
  ) {
    const prevRecord = prev as Record<string, unknown>;
    const nextRecord = next as Record<string, unknown>;
    const prevKeys = Object.keys(prevRecord);
    const nextKeys = Object.keys(nextRecord);
    if (prevKeys.length !== nextKeys.length) return false;
    return prevKeys.every((key) => Object.is(prevRecord[key], nextRecord[key]));
  }
  return false;
}

function updateTable(
  tableId: string,
  updater: (slice: TableSlice) => TableSlice
): TableSlice {
  const prev = getSlice(tableId);
  const next = updater(prev);
  if (next === prev) return prev;
  useTableClientStore.setState((state) => ({
    tables: {
      ...state.tables,
      [tableId]: next,
    },
  }));
  return next;
}

export function resetTableClientStore(): void {
  useTableClientStore.setState({ tables: {} });
}

/**
 * Write-only table field setter. Does not subscribe, matching Recoil
 * `useSetRecoilState` so effects that write page records / filters cannot
 * re-render their owner and loop.
 */
function useSetTableField<K extends keyof TableSlice>(
  tableId: string,
  field: K
): (updater: SetStateAction<TableSlice[K]>) => void {
  return useCallback(
    (updater: SetStateAction<TableSlice[K]>) => {
      const prev = getSlice(tableId);
      const nextValue = resolveUpdater(prev[field], updater);
      if (fieldValuesEqual(prev[field], nextValue)) return;

      const next = updateTable(tableId, (slice) => ({
        ...slice,
        [field]: nextValue,
      }));

      if (field === 'density') {
        persistTableSetting(
          tableId,
          'density',
          next.density,
          next.density === undefined
        );
      }
      if (field === 'resultsPerPage') {
        persistTableSetting(
          tableId,
          'resultsPerPage',
          next.resultsPerPage,
          next.resultsPerPage === undefined
        );
      }
    },
    [tableId, field]
  );
}

function useTableField<K extends keyof TableSlice>(
  tableId: string,
  field: K,
  fallback: TableSlice[K]
): [TableSlice[K], (updater: SetStateAction<TableSlice[K]>) => void] {
  const value = useTableClientStore((s) => {
    const existing = s.tables[tableId];
    if (existing) return existing[field];
    if (field === 'density' || field === 'resultsPerPage') {
      return hydrateSlice(tableId)[field];
    }
    return fallback;
  });
  const setValue = useSetTableField(tableId, field);
  return [value, setValue];
}

export function useTablePageIndex(tableId: string) {
  return useTableField(tableId, 'pageIndex', 0);
}

export function useSetTablePageIndex(tableId: string) {
  return useSetTableField(tableId, 'pageIndex');
}

export function useTablePageSize(tableId: string) {
  return useTableField(tableId, 'pageSize', PAGE_SIZE);
}

export function useTablePageRecords(tableId: string) {
  return useTableField(tableId, 'pageRecords', EMPTY_RECORDS);
}

export function useSetTablePageRecords(tableId: string) {
  return useSetTableField(tableId, 'pageRecords');
}

export function useTableTotalRecords(tableId: string) {
  return useTableField(tableId, 'totalRecords', 0);
}

export function useSetTableTotalRecords(tableId: string) {
  return useSetTableField(tableId, 'totalRecords');
}

export function useTableFilters(tableId: string) {
  return useTableField(tableId, 'filters', EMPTY_FILTERS);
}

export function useSetTableFilters(tableId: string) {
  return useSetTableField(tableId, 'filters');
}

export function useTableSorting(tableId: string) {
  return useTableField(tableId, 'sorting', EMPTY_SORTING);
}

export function useSetTableSorting(tableId: string) {
  return useSetTableField(tableId, 'sorting');
}

export function useTableDensity(tableId: string) {
  return useTableField(tableId, 'density', 'default');
}

export function useTableResultsPerPage(tableId: string) {
  return useTableField(tableId, 'resultsPerPage', undefined);
}

export { PAGE_SIZE };
